import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';

// Filter out the "No coverage" option (id: 0) from man months dropdown
const manMonthsOptions = lookupsJson.farm.manMonthsOptions.filter((opt) => opt.id !== 0);

const EmployersLiability = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values } = useFormikContext();

  // "Total man months (per year)" is a Forreston-only rate-structure input; CFM does not use it
  // and hides it via this brand flag (#33964).
  const hideManMonths = appConfig?.featureFlags?.hideFarmManMonths;

  const employees = getIn(values, 'farmLiabilityCoverage.employees') || {};
  const numManMonths = getIn(values, 'farmLiabilityCoverage.numManMonths') || 0;
  const excludedEmployeesDescription =
    getIn(values, 'farmLiabilityCoverage.exclusions.excludedEmployeesDescription') || '';

  // #35073: "Total Part-Time Man-Days" is the form/output value; the companion employee-COUNT
  // field is what rating reads. Reveal the count once man-days are entered (or when a count
  // already exists, e.g. a migrated offer), matching the two-field layout stakeholders asked for.
  const partTime40DaysOrLessManDays = employees.partTime40DaysOrLess || 0;
  const partTime40DaysOrLessEmployees = employees.partTime40DaysOrLessEmployees || 0;
  const showPartTime40DaysOrLessEmployees = partTime40DaysOrLessManDays > 0 || partTime40DaysOrLessEmployees > 0;
  // On CFM (hideFarmManMonths) the count is now a hard schema requirement when man-days are
  // entered (#35153 — the count field is always visible in that case, so it can't repeat the
  // #33009 hidden-required lockout). On the man-months brands (Forreston), which don't get that
  // requirement, keep the original #35073 non-blocking nudge: man-days entered but a rating count
  // of 0 would silently rate this class as 0 employees.
  const showEmployeeCountWarning =
    !hideManMonths && partTime40DaysOrLessManDays > 0 && partTime40DaysOrLessEmployees === 0;

  const initialOpen =
    numManMonths > 0 ||
    employees.fullTime > 0 ||
    employees.partTime > 0 ||
    partTime40DaysOrLessManDays > 0 ||
    partTime40DaysOrLessEmployees > 0 ||
    !!excludedEmployeesDescription;

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.employersLiability">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Employer's liability - farm employees</div>}
        contentRight={initialOpen ? <AppliedTag /> : null}
        initialOpen={initialOpen}
      >
        <Grid container spacing={2}>
          {/* Labels forced to shrink so long day-range text doesn't overlap the
              numeric value (#30499.1). Day-range detail lives in the tooltip on
              each field - the visible labels are kept short so the three
              xs={4} columns don't crash into each other (#30632). */}
          <Field
            xs={4}
            type="numeric"
            id="farmLiabilityCoverage.employees.fullTime"
            name="farmLiabilityCoverage.employees.fullTime"
            mode="dark"
            label="Number of Full-Time Employees"
            labelProps={{ shrink: true }}
            tooltipText="Total number of full time farm employees working 180 days per year or more."
            disabled={disabled}
            onChange={onChangeActive}
          />
          <Field
            xs={4}
            type="numeric"
            id="farmLiabilityCoverage.employees.partTime"
            name="farmLiabilityCoverage.employees.partTime"
            mode="dark"
            label="Number of Part-Time Employees"
            labelProps={{ shrink: true }}
            tooltipText="Total number of part time farm employees working over 40 days, but less than 180 days per year."
            disabled={disabled}
            onChange={onChangeActive}
          />
          <Field
            xs={4}
            type="numeric"
            id="farmLiabilityCoverage.employees.partTime40DaysOrLess"
            name="farmLiabilityCoverage.employees.partTime40DaysOrLess"
            mode="dark"
            label="Total Part-Time Man-Days"
            labelProps={{ shrink: true }}
            tooltipText="Total number of man-days for part time farm employees working 40 days or less per year."
            disabled={disabled}
            onChange={onChangeActive}
          />
          {/* #35073: the man-days field above is required on the form, but rating needs a
              headcount. This companion count is what the rater reads (num_employee_under_41);
              it appears once man-days are entered. */}
          {showPartTime40DaysOrLessEmployees && (
            <Field
              xs={4}
              type="numeric"
              id="farmLiabilityCoverage.employees.partTime40DaysOrLessEmployees"
              name="farmLiabilityCoverage.employees.partTime40DaysOrLessEmployees"
              mode="dark"
              label="Number of Part-Time Employees (40 Days or Less)"
              labelProps={{ shrink: true }}
              tooltipText="Number of part time farm employees working 40 days or less per year. Rating uses this count, not the man-days value."
              disabled={disabled}
              onChange={onChangeActive}
            />
          )}
          {showEmployeeCountWarning && (
            <Grid item xs={12}>
              <div className={classes.employeeCountWarning}>
                Man-days are entered but the part-time employee count is 0 — this liability will rate as 0 employees
                until you enter a count.
              </div>
            </Grid>
          )}
          {!hideManMonths && (
            <Field
              xs={12}
              type="select"
              id="farmLiabilityCoverage.numManMonths"
              name="farmLiabilityCoverage.numManMonths"
              mode="dark"
              label="Total man months (per year)"
              options={manMonthsOptions}
              disabled={disabled}
              onChange={onChangeActive}
            />
          )}
          <Field
            xs={12}
            type="textarea"
            id="farmLiabilityCoverage.exclusions.excludedEmployeesDescription"
            name="farmLiabilityCoverage.exclusions.excludedEmployeesDescription"
            mode="dark"
            label="Farm Employees Excluded from Coverage"
            tooltipText="Describe any farm employees excluded from employer's liability coverage."
            disabled={disabled}
            onChange={onChangeActive}
          />
        </Grid>
      </SubRiskCollapsibleSection>
    </div>
  );
};

EmployersLiability.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default EmployersLiability;
